/**
 * The preview's OWN zoom, for a frame too narrow for Excalidraw's desktop layout (R10-CX-252). Excalidraw 0.18 reads its container's real size and switches to
 * the mobile layout (no zoom control, the menu in a bottom bar) when it is narrower than 730px, or shorter than 500px and narrower than 1000px
 * (`isMobileBreakpoint`, MQ_MAX_WIDTH_PORTRAIT / MQ_MAX_HEIGHT_LANDSCAPE / MQ_MAX_WIDTH_LANDSCAPE in the installed `@excalidraw/excalidraw`); it has no prop or
 * `UIOptions` to force the desktop layout, and the prototype never hits it because its record is wide (`Boards.tsx:310`, `DrawingCanvas.tsx:35`). In that case
 * the preview draws the engine's own zoom control (its markup and stylesheet, see the record tab template) and acts like the engine's own zoom actions.
 */
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 30;

export const EXCALIDRAW_MOBILE_WIDTH = 730;
export const EXCALIDRAW_MOBILE_HEIGHT = 500;
export const EXCALIDRAW_LANDSCAPE_WIDTH = 1000;

export function isExcalidrawMobile(width: number, height: number): boolean {
  return width < EXCALIDRAW_MOBILE_WIDTH || (height < EXCALIDRAW_MOBILE_HEIGHT && width < EXCALIDRAW_LANDSCAPE_WIDTH);
}

interface ZoomApi {
  getAppState(): { zoom: { value: number }; scrollX: number; scrollY: number; width: number; height: number };
  updateScene(scene: { appState: object }): void;
}

/** The engine's own zoom step, bounds and rounding (`ZOOM_STEP` 0.1, `MIN_ZOOM` 0.1, `MAX_ZOOM` 30, `getNormalizedZoom` rounds to 6 places and clamps). */
export const ZOOM_STEP = 0.1;
export const normalizeZoom = (zoom: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(zoom * 1e6) / 1e6));

/**
 * Sets the zoom the way the engine's own zoomIn, zoomOut and resetZoom actions do (`getStateForZoom` about the middle of the visible frame; screen = (scene + scroll) * zoom, so
 * the scroll shifts by half the frame in scene units). The engine reports the new zoom through its change callback; the caller reads it from there. False when there is no engine.
 */
export function zoomPreviewTo(api: ZoomApi | undefined, target: number): boolean {
  if (!api) return false;
  const state = api.getAppState();
  const before = state.zoom.value;
  const after = normalizeZoom(target);
  api.updateScene({
    appState: {
      zoom: { value: after },
      scrollX: state.scrollX + state.width / 2 / after - state.width / 2 / before,
      scrollY: state.scrollY + state.height / 2 / after - state.height / 2 / before,
    },
  });
  return true;
}
